/**
 * Market display utilities — pure formatters and timezone helpers.
 *
 * Note: market sentiment / spread CLASSIFICATION is computed by the backend
 * (see GET /api/market/context). The frontend only renders the result; we
 * do NOT recompute weights or thresholds client-side. If backend is down,
 * UI shows "loading"/"error" state, never a silent fallback.
 */

// ===== ET (Eastern Time) Utilities =====

export const ET_TIMEZONE = 'America/New_York';

export const getETNow = () => {
  const etString = new Date().toLocaleString('en-US', { timeZone: ET_TIMEZONE });
  return new Date(etString);
};

export const getETDateString = () => {
  const et = getETNow();
  return `${et.getFullYear()}-${String(et.getMonth() + 1).padStart(2, '0')}-${String(et.getDate()).padStart(2, '0')}`;
};

export const getETTimeId = () => {
  const et = getETNow();
  return `${String(et.getHours()).padStart(2, '0')}${String(et.getMinutes()).padStart(2, '0')}${String(et.getSeconds()).padStart(2, '0')}`;
};

export const getETDateTimeString = () => {
  const et = getETNow();
  const date = getETDateString();
  const time = `${String(et.getHours()).padStart(2, '0')}:${String(et.getMinutes()).padStart(2, '0')}:${String(et.getSeconds()).padStart(2, '0')}`;
  return `${date}T${time}`;
};

// ===== Sentiment Display Helpers =====
// Sentiment values come from backend; these helpers map to visuals.

/**
 * Format sentiment label with backend-computed trend score.
 * Use null for trendScore when not available (loading/error state).
 */
export const formatMarketSentiment = (trendScore, sentiment) => {
  const sentimentName = sentiment === 'loading' ? 'Loading'
    : sentiment === 'error' ? 'Error'
    : sentiment ? sentiment.charAt(0).toUpperCase() + sentiment.slice(1)
    : 'Unknown';
  if (trendScore == null) return sentimentName;
  return `${trendScore >= 0 ? '+' : ''}${trendScore.toFixed(2)}% ${sentimentName}`;
};

export const getSentimentEmoji = (sentiment) => {
  switch (sentiment) {
    case 'bullish': return '🟢';
    case 'mixed':   return '🟡';
    case 'bearish': return '🔴';
    case 'error':   return '⚠️';
    default:        return '⏳';
  }
};

export const getSentimentColor = (sentiment) => {
  switch (sentiment) {
    case 'bullish': return '#16a34a';
    case 'mixed':   return '#ca8a04';
    case 'bearish': return '#dc2626';
    case 'error':   return '#dc2626';
    default:        return '#6b7280';
  }
};

// ===== Spread Class Display Helpers =====
// IWM-SPY spread classification from backend; maps to visuals.

export const getSpreadEmoji = (spreadClass) => {
  switch (spreadClass) {
    case 'severe-narrow':     return '🚨';
    case 'mild-narrow':       return '⚠️';
    case 'small-cap-leading': return '📈';
    case 'balanced':          return '📊';
    default:                  return '📊';
  }
};

export const getSpreadColor = (spreadClass) => {
  switch (spreadClass) {
    case 'severe-narrow':
      return { bg: '#fef2f2', border: '#fecaca', text: '#dc2626' };
    case 'mild-narrow':
      return { bg: '#fefce8', border: '#fef08a', text: '#ca8a04' };
    case 'small-cap-leading':
      return { bg: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' };
    case 'balanced':
      return { bg: '#f9fafb', border: '#e5e7eb', text: '#6b7280' };
    default:
      return { bg: '#f9fafb', border: '#e5e7eb', text: '#6b7280' };
  }
};
